import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { AxiosError, AxiosResponse } from 'axios';
import { ProductVariant } from '@/models/ProductVariant';
import AddPercentageDialog from '@/components/add-percentage-dialog/AddPercentageDialog.vue';
import ErrorHandler from '@/support/ErrorHandler';
import MiFileUpload from '@/components/mi-file-upload/MiFileUpload';
import { addClass, removeClass } from '@/support/Html';
import LoaderCard from '@/components/loader-card/LoaderCard';
import { Brand } from '@/models/Brand';
import { Supplier } from '@/models/Supplier';
import { Season } from '@/models/Season';
import debounce from 'debounce';
import { Category } from '@/models/Category';
import { Tag } from '@/models/Tag';
import { fileToBase64, formatDate as format } from '@/support/String';
import { objectToFormdata } from '@/support/Client';
import { Color } from '@/models/Color';
import { SubCategory } from '@/models/SubCategory';
import RenderFlow from '@/support/RenderFlow';
import MiProgressLinear from "@/components/mi-progress-linear/MiProgressLinear";
import { Attribute } from '@/models/Attribute';
import _ from 'lodash';
import { Material } from '@/models/Material';
import { Product } from '@/models/Product';

@Component<BulkUpdateProducts>({
  components: {
    AddPercentageDialog,
    MiFileUpload,
    LoaderCard,
    MiProgressLinear,
  },
  filters : {
    formatDate(date: string) {
      return !date ? '-' : format(date, 'dd-LL-yyyy');
    },
  },
})
export default class BulkUpdateProducts extends Vue {
  @Prop({ default: null })
  protected products!: ProductVariant[];

  protected selected: any[] = [];
  protected isLoading: boolean = false;
  protected isLoadingPercentage: number = 0;
  protected activeStepper: boolean = true;
  protected currentStep: number = 1;

  protected isApproved: boolean = false;
  protected editIsApproved: boolean = false;
  protected reasonDeclined: string = "";

  protected name = '';

  protected isUploadingCsv: boolean = false;
  protected selectedCsvFile: File | null = null;

  protected selectedCategories: Category[] | null = null;

  protected selectedProduct: Product | null = null;

  private RenderFlow = new RenderFlow(['isShowingBlockDatePicker','isShowingFinalReturnDatePicker']);

  protected seasonsAvailableForUpdate: Season[] | null = null;
  protected selectedSeason: Season | null = null;

  protected tags: Tag[] | null = null;
  protected selectedTags: Tag[] | null = null;

  protected materials: Material[] | null = null;
  protected selectedMaterials: Material[] | null = null;
  protected isEditingMaterials = false;
  protected selectedMaterial: Material | null = null;
  protected selectedPercentage = 0;

  protected defaultImage: number | null = null;

  protected brands: Brand[] = [];
  protected suppliers: Supplier[] = [];
  protected colors: Color[] = [];

  protected updateErrors: updateError[] = [];

  protected uploadedProducts: ProductVariant[] = [];

  protected productsWithoutColor: ProductVariant[] = [];

  protected statuses: Status[] = [
    {
      text: "Concept",
      value: "concept"
    },
    {
      text: "Active",
      value: "active"
    },
    {
      text: "Inactive",
      value: "inactive"
    }
  ];

  /**
 * used by ownership select
 */
  public ownershipOptions: object[] = [
    { id: "maison", name: "Maison" },
    { id: "supplier", name: "Supplier" }
  ];

  protected selectedBrand: string = "";
  protected selectedSupplier: string = "";
  protected selectedColor: string = "";
  protected selectedOwnership: string = "";
  protected selectedStatus: string = "";
  protected selectedBlockDate: string = "";
  protected selectedFinalReturnDate: string = "";
  protected selectedRetailPrice: string = "";
  protected selectedWholesalePrice: string = "";
  protected selectedBuyPrice: string = "";
  protected isRerun = false;

  protected uploadedFiles: UploadedFile[] = [];
  protected acceptedFileTypes = ["image/png", "image/jpeg", "image/svg+xml"];

  protected uploadedFile: string = "";

  public searchBrandDebounce: Function = this.handleBrandSearch();
  public searchSupplierDebounce: Function = this.handleSupplierSearch();
  public searchColorDebounce: Function = this.handleColorSearch();
  public searchMaterialDebounce: Function = this.handleMaterialSearch();
  public searchSeasonDebounce: Function = this.handleSeasonSearch();
  public searchTagDebounce: Function = this.handleTagSearch();
  protected brandSearch: string = "";
  protected supplierSearch: string = "";
  protected colorSearch: string = "";
  protected materialSearch: string = "";
  protected tagSearch: string = "";
  protected seasonSearch: string = "";

  protected attributes: Attribute[] = [];
  protected selectedAttributes: attributeItem[] = [];
  protected activeAttribute = '';

  protected handleBrandSearch() {
    return debounce((searchInput: string) => {
      this.getBrands(searchInput);
    }, 400);
  }

  protected handleSupplierSearch() {
    return debounce((searchInput: string) => {
      this.getSuppliers(searchInput);
    }, 400);
  }

  protected handleColorSearch() {
    return debounce((searchInput: string) => {
      this.getColors(searchInput);
    }, 400);
  }

  protected handleSeasonSearch() {
    return debounce((searchInput: string) => {
      this.getSeasons(searchInput);
    }, 400);
  }

  protected handleTagSearch() {
    return debounce((searchInput: string) => {
      this.getTags(searchInput);
    }, 400);
  }

  protected handleMaterialSearch() {
    return debounce((searchInput: string) => {
      this.getMaterials(searchInput);
    }, 400);
  }

  protected categories: Category[] | null = null;
  protected categorySearch: string = "";
  protected searchCategoryDebounce: Function = debounce((searchInput: string) => this.getCategories(searchInput), 400);

  mounted() {
    const pageHtml = document.querySelectorAll("html")[0];
    addClass(pageHtml, "overflow-y-hidden");

    this.getAttributes();
    this.getBrands();
    this.getSuppliers();
    // this.getCategories(); // Modified so it loads the colours when focussing the autocomplete
    // this.getColors();
    this.getSeasons();
    this.getTags();
    this.getMaterials();
  }

  protected removeMaterial(material: Material): void {
    if (!this.selectedMaterials) {
      return;
    }
    this.selectedMaterials = this.selectedMaterials.filter(function(indexedMaterial) { return indexedMaterial.id !== material.id; });
  }

  protected addPercentageToMaterial(material: Material): void {
    this.selectedMaterial = material;
    this.isEditingMaterials = true;
  }

  protected closeEditMaterials(): void {
    this.selectedMaterial = null;
    this.isEditingMaterials = false;
  }

  protected saveMaterial(material: Material): void {
    if (!this.selectedMaterial) {
      return;
    }
    this.selectedMaterial = material;
    this.isEditingMaterials = false;
  }

  protected toggleAddAttributeOption(attribute: string, option: string) {
    if (this.isOptionSelected(attribute, option)) {
      const clearedAttributes = this.selectedAttributes.filter((currentAttribute: attributeItem) => {
        return (currentAttribute.attribute !== attribute) || (currentAttribute.attribute === attribute && currentAttribute.option !== option);
      });

      this.$set(this, 'selectedAttributes', clearedAttributes);
    } else {
      const attributeFilter: attributeItem = { attribute, option };
      this.selectedAttributes.push(attributeFilter);
    }
  }

  protected isOptionSelected(attribute: string, option: string) {
    return this.selectedAttributes.some((currentAttribute: attributeItem) => {
      return currentAttribute.attribute === attribute && currentAttribute.option === option;
    });
  }

  protected getBrands(filter: string = "") {
    new Brand()
      .filter("search", filter)
      .limit(100)
      .all()
      .then((brands: Brand[]) => {
        this.brands = brands;
      })
      .catch((error: AxiosError) => {});
  }

  protected getSuppliers(filter: string = "") {
    new Supplier()
      .filter("search", filter)
      .limit(100)
      .all()
      .then((suppliers: Supplier[]) => {
        this.suppliers = suppliers;
      })
      .catch((error: AxiosError) => {});
  }

  protected getColors(filter: string = "") {
    new Color()
      .filter("search", filter)
      .limit(999) // so we can also search inside all the sub-items
      .all()
      .then((colors: Color[]) => {
        let parsedColors: Color[] = [];
        colors.forEach((color: any) => {
          parsedColors.push(color);
          if (color.sub_colors && color.sub_colors.length > 1) {
            color.sub_colors.forEach((subColor: Color) => {
              parsedColors.push(subColor);
            });
          }
        });

        this.colors = parsedColors;
      })
      .catch((error: AxiosError) => {});
  }

  protected getSeasons(filter: string = "") {
    new Season()
      .filter("search", filter)
      .limit(100)
      .all()
      .then((seasonResponse: Season[]) => {
        this.seasonsAvailableForUpdate = seasonResponse;
      })
      .catch((error: AxiosError) => {});
  }

  protected getTags(filter: string = "") {
    new Tag()
      .filter("search", filter)
      .limit(100)
      .all()
      .then((tags: Tag[]) => {
        this.tags = tags;
      })
      .catch((error: AxiosError) => {});
  }

  protected getMaterials(filter: string = "") {
    new Material()
      .filter("search", filter)
      .limit(100)
      .all()
      .then((materials: any) => {
        this.materials = materials;
        if (!this.materials) {
          return;
        }
        this.materials = this.materials.map((material: Material) => ({ ...material, percentage: 100 } as Material));
      })
      .catch((error: AxiosError) => {});
  }

  protected goToStep(step: number) {
    if (this.currentStep < step || !this.activeStepper) {
      return;
    }

    this.currentStep = step;
  }

  protected get hasBrandAndSupplier() {
    return this.selectedBrand.length && this.selectedSupplier.length;
  }

  protected close() {
    this.$emit("input", false);
    this.currentStep = 1;
    this.clear();
    const pageHtml = document.querySelectorAll('html')[0];
    removeClass(pageHtml, 'overflow-y-hidden');
  }

  protected clear() {
    this.selectedBrand = "";
    this.selectedSupplier = "";
    this.selectedColor = "";
    this.selectedOwnership = "";
    this.selectedStatus = "";
    this.selectedBlockDate = "";
    this.selectedFinalReturnDate = "";
    this.selectedTags = null;
    this.selectedSeason = null;
    this.selectedCsvFile = null;
    this.isRerun = false;
    this.$emit("clearSelected");
  }

  protected handleItemDropped(file: File) {
    if (!file) {
      return;
    }

    fileToBase64(file).then((encodedFile: string) => {
      const uploadedFile: UploadedFile = {
        url: encodedFile,
        name: file.name,
        file: file,
        default: false,
      };
      this.uploadedFiles.push(uploadedFile);
    });

    const fileUploadComponent = this.$refs.fileUpload as MiFileUpload;
    fileUploadComponent.uploaded();
  }

  protected async create() {
    this.isLoading = true;
    this.currentStep = 5;
    await this.addCategories();
    await this.addAttributes();
    await this.updateProducts();
    await this.uploadImage();
    this.isLoading = false;

    if (!this.updateErrors.length) {
      this.finishUpdate();
    }
  }

  protected addIsLoadingStep() {
    // Category + products + images
    const totalIterations = (this.products.length * 2) + this.uploadedFiles.length;
    const step = 100 / totalIterations;
    this.isLoadingPercentage += step;
  }

  protected getError(id: string) {
    const productError = this.updateErrors.find((error: updateError) => {
      return error.product === id;
    });

    if (productError && productError.error) {
      return productError.error.response;
    }

    return {};
  }

  protected hasErrors(id: string) {
    return !! this.updateErrors.find((error: updateError) => {
      return error.product === id;
    });
  }

  protected isUploaded(id: string) {
    return !! this.uploadedProducts.find((product: ProductVariant) => {
      return product.id === id;
    });
  }


  protected async getAttributes() {
    await new Attribute().include('options')
      .limit(100)
      .all()
      .then((attributes: Attribute[]) => {
        this.attributes = attributes;

      })
      .catch((error: AxiosError) => {});
  }

  protected async updateProducts() {
    const productPromises = this.products.map(product => this.updateProduct(product));
    await Promise.all(productPromises);

    if (this.updateErrors) {
      return;
    }

    this.isLoading = false;
    this.finishUpdate();
  }

  protected async updateProduct(product: ProductVariant) {
    const payload = this.createPayload();

    this.addIsLoadingStep();

    await new ProductVariant(product)
      .update(payload)
      .then((uploadedProduct: ProductVariant) => {
        this.uploadedProducts.push(uploadedProduct);
      })
      .catch((error: AxiosError) => {
        this.updateErrors.push({ product: product.id, error: error });
        // this.isLoading = false;
      });

  }

  protected async addCategories() {
    for (let product of this.products) {
      this.addIsLoadingStep();
      if (!this.selectedCategories) {
        return;
      }

      this.isLoading = true;

      for (let category of this.selectedCategories) {
        if (!product || !product.product_uuid || !category.id) {
          return;
        }

        await new ProductVariant()
          .attachCategory(product.product_uuid, category.id)
          .then((response: AxiosResponse) => {
            //
          })
          .catch((error: AxiosError) => {
            ErrorHandler.network(error);
          });
      }
    }
  }

  protected async addAttributes() {
    const attributePromises = this.selectedAttributes.map(attribute => this.attachAttribute(attribute));
    await Promise.all(attributePromises);
  }

  protected async attachAttribute(entry: attributeItem) {
    if (!this.products) {
      return;
    }

    const variants = this.products.map(product => product.id);

    let payload = {
      product_variants: variants,
      attribute: entry.attribute,
      option: entry.option,
    };

    new Attribute()
    .addAttributeToProducts(payload)
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    });
  }

  protected get attributeSummary() {
    const grouped = this.selectedAttributes.filter((attribute, index, self) =>
      index === self.findIndex((indexed) => (
        indexed.attribute === attribute.attribute
      )));

      const test = grouped.map((attribute: attributeItem) => {
        return {
          attribute: attribute.attribute,
          options: this.getSelectedOptions(`${attribute.attribute}`),
        };
      });
      return test;
  }

  protected getSelectedOptions(attribute_id: string) {
    const attributes = this.selectedAttributes.filter((indexedAttribute: attributeItem) => {
      return indexedAttribute.attribute === attribute_id;
    });

    return attributes.map(attribute => attribute.option);
  }

  protected getAttributeById(attribute_id: string) {
    return this.attributes.find((indexedAttribute: Attribute) => {
      return indexedAttribute.id === attribute_id;
    });
  }

  protected getOptionById(attribute_id: string, option_id: string) {
    const attribute = this.attributes.find((indexedAttribute: Attribute) => {
      return indexedAttribute.id === attribute_id;
    });

    if(attribute && attribute.options) {
      return attribute.options.find((option: Record<string, any>) => {
        return option.id === option_id;
      });

    }

    return {};
  }

  protected async uploadImage() {
    if (!this.uploadedFiles || !this.uploadedFiles.length) {
      return;
    }

    this.isLoading = true;
    for (let file of this.uploadedFiles) {
      this.addIsLoadingStep();

      let variants: string[] = this.products.map((product: ProductVariant) => {
        return product.id ? product.id : "";
      });

      let payload: { [key: string]: any } = {
        product_variants: variants,
        image: file.file,
        default: file.default,
      };

      await new ProductVariant()
        .attachImage()
        .create(objectToFormdata(payload))
        .then((response: AxiosResponse) => {
          //
        })
        .catch((error: AxiosError) => {
          ErrorHandler.network(error);
        });
    };
  }

  protected async getCategories(filter: string = ""): Promise<void> {
    await new Category()
      .filter("search", filter)
      .limit(999) // so we can also search inside all the sub-items
      .all()
      .then((categories: Category[]) => {
        let parsedCategories: Category[] = [];
        categories.forEach((category: any) => {
          parsedCategories.push(category);
          if (category.sub_categories && category.sub_categories.length > 1) {
            category.sub_categories.forEach((subColor: Category) => {
              parsedCategories.push(subColor);
            });
          }
        });

        this.categories = parsedCategories;
      })
      .catch((error: AxiosError) => {});
  }

  private finishUpdate() {
    this.$emit("productsUpdated");
    this.isLoading = false;
    this.isLoadingPercentage = 0;
  }

  protected createPayload() {
    let payload = {
      is_rerun: this.isRerun,
    };

    if (this.selectedBrand) {
      payload = {
        ...payload,
        ...{
          brand: this.selectedBrand
        }
      };
    }

    if (this.selectedSupplier) {
      payload = {
        ...payload,
        ...{
          supplier: this.selectedSupplier
        }
      };
    }

    if (this.name) {
      payload = {
        ...payload,
        ...{
          name: this.name
        }
      };
    }

    if (this.selectedSeason) {
      payload = {
        ...payload,
        ...{
          season: this.selectedSeason
        }
      };
    }

    if (this.selectedOwnership) {
      payload = {
        ...payload,
        ...{
          ownership: this.selectedOwnership
        }
      };
    }

    if (this.selectedRetailPrice) {
      payload = {
        ...payload,
        ...{
          retail_price: this.selectedRetailPrice
        }
      };
    }

    if (this.selectedWholesalePrice) {
      payload = {
        ...payload,
        ...{
          wholesale_price: this.selectedWholesalePrice
        }
      };
    }

    if (this.selectedBuyPrice) {
      payload = {
        ...payload,
        ...{
          buy_price: this.selectedBuyPrice
        }
      };
    }

    if (this.selectedColor) {
      payload = {
        ...payload,
        ...{
          color: this.selectedColor
        }
      };
    }

    if (this.selectedStatus) {
      payload = {
        ...payload,
        ...{
          status: this.selectedStatus
        }
      };
    }

    if (this.selectedBlockDate) {
      payload = {
        ...payload,
        ...{
          block_date: this.selectedBlockDate
        }
      };
    }

    if (this.selectedFinalReturnDate) {
      payload = {
        ...payload,
        ...{
          final_return_date: this.selectedFinalReturnDate
        }
      };
    }

    if (this.editIsApproved) {
      payload = {
        ...payload,
        ...{
          is_approved: this.isApproved
        }
      };
    }

    if (this.isApproved) {
      payload = {
        ...payload,
        ...{
          comment: ""
        }
      };
    }

    if (this.reasonDeclined) {
      payload = {
        ...payload,
        ...{
          comment: this.reasonDeclined
        }
      };
    }

    if (this.selectedTags) {
      payload = {
        ...payload,
        ...{
          tags: this.selectedTags.map(selectedTag => selectedTag.id)
        }
      };
    }
    if (this.selectedMaterials) {
      payload = {
        ...payload,
        ...{
          materials: this.selectedMaterials.map(selectedMaterial => {
            return {
              id: selectedMaterial.id,
              percentage: selectedMaterial.percentage ? selectedMaterial.percentage : 0,
            }
          })
        }
      };
    }

    if (this.selectedProduct && this.selectedProduct.id) {
      payload = {
        ...payload,
        ...{
          product: this.selectedProduct.id,
        }
      }
    }

    return payload;
  }

  protected deleteImage(index: number) {
    this.uploadedFiles.splice(index, 1);
  }

  protected setDefaultImage(index: number) {
    // Set all to false
    for (let file of this.uploadedFiles) {
      file.default = false;
    }

    // Set this one to true
    this.uploadedFiles[index].default = true;
  }

  protected get chosenBrand() {
    const brand = this.brands.filter((brand: Brand) => {
      return brand.id === this.selectedBrand;
    });

    return brand;
  }

  protected get chosenSupplier() {
    const supplier = this.suppliers.filter((supplier: Supplier) => {
      return supplier.id === this.selectedSupplier;
    });

    return supplier;
  }

  protected get chosenColor() {
    const color = this.colors.filter((color: Color) => {
      return color.id === this.selectedColor;
    });

    return color;
  }

  protected get chosenStatus() {
    const status = this.statuses.filter((status: Status) => {
      return status.value === this.selectedStatus;
    });

    return status;
  }

  protected get requiresColor() {
    this.productsWithoutColor = [];
    for (const product of this.products) {
      // @ts-ignore It's an empty array if empty, but an object if filled
      if (Array.isArray(product.color)) {
          this.productsWithoutColor.push(product);
      }
    }

    return this.selectedStatus === 'active' && this.productsWithoutColor.length > 0;
  }

  protected get hasRequiredColor() {
    if (this.requiresColor) {
      return this.selectedColor !== '';
    }

    return true;
  }

  protected goToNextStep() {
    if (! this.hasRequiredColor) {
      return;
    }

    this.currentStep = 3;
  }

  protected get productModel(): Product {
    return new Product();
  }

  @Watch("brandSearch")
  protected brandSearchChanged(brand: string) {
    this.searchBrandDebounce(brand);
  }

  @Watch("supplierSearch")
  protected supplierSearchChanged(brand: string) {
    this.searchSupplierDebounce(brand);
  }

  @Watch("colorSearch")
  protected colorSearchChanged(color: string) {
    this.searchColorDebounce(color);
  }

  @Watch("tagSearch")
  protected tagChanged(tag: string) {
    this.searchTagDebounce(tag);
  }
  @Watch("seasonSearch")
  protected seasonChanged(season: string) {
    this.searchSeasonDebounce(season);
  }

  @Watch("categorySearch")
  protected categorySearchChanged(category: string) {
    this.searchCategoryDebounce(category);
  }
}

interface UploadedFile {
  url?: string;
  name?: string;
  file?: any;
  default: boolean,
}

interface Status {
  value?: string;
  text?: string;
}

interface updateError {
  product?: string;
  error?: AxiosError;
}

interface attributeItem {
  attribute?: string;
  option?: string;
}
